import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { useCommand } from '../context/CommandContext';

// Import builder components
import BuilderLayout from '../components/agent-builder/BuilderLayout';
import BuilderHeader from '../components/agent-builder/BuilderHeader';
import EntryPointSelector from '../components/agent-builder/common/EntryPointSelector';
import LivePreviewPanel from '../components/agent-builder/preview/LivePreviewPanel';
import TestInputPanel from '../components/agent-builder/test/TestInputPanel';
import TestResultModal from '../components/agent-builder/test/TestResultModal';
import QuickActionsPanel from '../components/agent-builder/common/QuickActionsPanel';
import CapabilityFlowCanvas from '../components/agent-builder/flow/CapabilityFlowCanvas';
import VersionHistoryPanel from '../components/agent-builder/common/VersionHistoryPanel';
import RunHistoryPanel from '../components/agent-builder/common/RunHistoryPanel';
import ConfigPanel from '../components/agent-builder/config/ConfigPanel';
import { CreateOperatorModal } from './Operators';
import SubmissionModal from '../components/agent-builder/SubmissionModal';

import {
  ArrowLeft,
  CaretRight,
  ClockCounterClockwise,
  Copy,
  FileText,
  FloppyDisk,
  GearSix,
  Lightbulb,
  Robot,
  Rocket,
  Sparkle,
  Warning
} from '@phosphor-icons/react';
/**
 * UnifiedAgentBuilder - The Future of Agent Creation
 * 
 * A hybrid approach combining:
 * - Conversational AI chat builder
 * - Visual capability flow editor
 * - Live preview panel
 * - Test Input Panel with flow animation
 * - Quick actions panel
 * 
 * Entry Points (all equal):
 * - AI Builder (chat-first, recommended)
 * - Voice Agent (specialized wizard)
 * - From Opportunity (prefilled)
 * - From Template (marketplace)
 * - From Scratch (blank canvas)
 */
const UnifiedAgentBuilder = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { agentId } = useParams();
  const { activeOrganization } = useOrganization();
  const { openCommandForAgent, agentModifiedSignal } = useCommand();
  
  // Entry point detection
  const entryPoint = searchParams.get('from'); // opportunity, template, scratch, clone, ai - null if not specified
  const sourceId = searchParams.get('sourceId'); // opportunityId or templateId
  const highlightParam = searchParams.get('highlight'); // comma-separated node IDs from Command modifications
  const highlightNodeIds = useMemo(() => highlightParam ? highlightParam.split(',').filter(Boolean) : [], [highlightParam]);
  
  // Builder state - default tab based on entry point
  const getDefaultMode = () => {
    // When editing an existing agent, default to visual flow
    if (agentId) {
      return 'visual';
    }
    switch (entryPoint) {
      case 'opportunity':
      case 'template':
      case 'command':
        return 'visual'; // Visual Flow for structured entry (including Command-created agents)
      case 'ai':
        return 'visual'; // Visual Flow for AI builder start (use Command for AI chat editing)
      case 'config':
      case 'scratch':
        return 'config'; // Config for manual setup
      default:
        return 'visual';
    }
  };
  
  const [mode, setMode] = useState(getDefaultMode());
  const [showEntrySelector, setShowEntrySelector] = useState(!entryPoint && !agentId); // Show selector when no entry point specified and not editing existing agent
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showOperatorModal, setShowOperatorModal] = useState(false); // For Operator Agent creation
  
  // Scroll to top when component mounts or entry point changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [entryPoint, agentId]);
  
  // Sync showEntrySelector and mode with URL parameters when they change
  useEffect(() => {
    if (entryPoint || agentId) {
      setShowEntrySelector(false);
      // Set appropriate mode based on context
      if (agentId && !entryPoint) {
        // Editing existing agent - show visual flow
        setMode('visual');
      } else {
        setMode(getDefaultMode());
      }
    } else {
      // No entry point and no agentId - show entry selector and reset state
      setShowEntrySelector(true);
      
      // Reset agent config to fresh state when showing entry selector
      const freshConfig = {
        name: '',
        description: '',
        capability_profile: 'text_agent',
        accepted_inputs: [],
        required_inputs: [],
        expected_outputs: [],
        required_capabilities: [],
        optional_capabilities: [],
        capability_bindings: {},
        system_prompt: '',
        is_active: false,
        status: 'draft',
        use_custom_ai_model: false,
        custom_ai_config: null,
        knowledge_base: [],
        advanced_settings: {
          system_prompt_override: '',
          variable_templates: {},
          output_format: 'text'
        }
      };
      setAgentConfig(freshConfig);
      setInitialConfigHash(hashConfig(freshConfig));
      setSourceData(null);
      setCapabilitiesLocked(false);
      setChatHistory([]);
    }
  }, [entryPoint, agentId]);
  
  // Agent configuration state
  const [agentConfig, setAgentConfig] = useState({
    name: '',
    description: '',
    capability_profile: 'text_agent',
    accepted_inputs: [],
    required_inputs: [],
    expected_outputs: [],
    required_capabilities: [],
    optional_capabilities: [],
    capability_bindings: {},
    system_prompt: '',
    is_active: false,
    status: 'draft',
    // Agent-specific AI model override
    use_custom_ai_model: false,
    custom_ai_config: null,
    // New fields for consolidated builder
    knowledge_base: [], // Files for persistent knowledge
    advanced_settings: {
      system_prompt_override: '',
      variable_templates: {},
      tool_parameters: {},
      webhook_headers: {},
      execution_constraints: {},
      allow_side_effects: false
    }
  });
  
  // Track if capabilities are locked (from opportunity)
  const [capabilitiesLocked, setCapabilitiesLocked] = useState(false);
  
  // Source data (opportunity/template)
  const [sourceData, setSourceData] = useState(null);
  
  // Chat history for AI builder
  const [chatHistory, setChatHistory] = useState([]);
  
  // Test console state
  const [testResult, setTestResult] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Execution animation state for visual flow
  const [executionState, setExecutionState] = useState(null); // { phase, currentNodeIndex }
  const [testInput, setTestInput] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [executionMetrics, setExecutionMetrics] = useState(null);
  const [testInputCollapsed, setTestInputCollapsed] = useState(false);

  // Version history state
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Run history state
  const [showRunHistory, setShowRunHistory] = useState(false);

  // Track unsaved changes
  const [initialConfigHash, setInitialConfigHash] = useState(null);
  
  // Draft limit modal state
  const [draftLimitModal, setDraftLimitModal] = useState({ show: false, draftCount: 0, maxDrafts: 3 });

  // Marketplace submission state
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [marketplaceStatus, setMarketplaceStatus] = useState(null);

  // Fetch marketplace submission status for existing agents
  useEffect(() => {
    if (!agentId) return;
    const fetchMktStatus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/marketplace/submissions/by-agent/${agentId}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          if (data.submission) setMarketplaceStatus(data.submission.status);
        }
      } catch { /* non-blocking */ }
    };
    fetchMktStatus();
  }, [agentId]);

  // Simple hash function to detect changes
  const hashConfig = useCallback((config) => {
    return JSON.stringify({
      name: config.name,
      description: config.description,
      capability_profile: config.capability_profile,
      accepted_inputs: config.accepted_inputs,
      required_capabilities: config.required_capabilities,
      system_prompt: config.system_prompt
    });
  }, []);

  // Check for unsaved changes whenever config updates
  useEffect(() => {
    if (initialConfigHash) {
      const currentHash = hashConfig(agentConfig);
      setHasUnsavedChanges(currentHash !== initialConfigHash);
    }
  }, [agentConfig, initialConfigHash, hashConfig]);

  // Auto-save draft with debounce (save 2 seconds after last change)
  const [lastAutoSave, setLastAutoSave] = useState(null);
  
  useEffect(() => {
    // Only auto-save if:
    // 1. We have unsaved changes
    // 2. Agent has a name (basic validation)
    // 3. We're not already saving
    // 4. We have an org context
    if (!hasUnsavedChanges || !agentConfig.name?.trim() || saving || !activeOrganization?.organization_id) {
      return;
    }

    const autoSaveTimer = setTimeout(async () => {
      try {
        // Create or update draft agent
        const endpoint = agentId 
          ? `${BACKEND_URL}/api/agents/${agentId}`
          : `${BACKEND_URL}/api/agents`;
        
        const method = agentId ? 'PUT' : 'POST';
        
        const response = await fetch(endpoint, {
          method,
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          body: JSON.stringify({
            ...agentConfig,
            status: 'draft',
            is_auto_save: true
          })
        });

        if (response.ok) {
          const data = await response.json();
          setLastAutoSave(new Date());
          
          // If this was a new agent, update the URL without full navigation
          if (!agentId && data.agent_id) {
            window.history.replaceState({}, '', `/unified-agent-builder/${data.agent_id}`);
          }
          
          // Update initial hash to mark as "saved"
          setInitialConfigHash(hashConfig(agentConfig));
          setHasUnsavedChanges(false);
          
          console.log('Auto-saved draft:', data.agent_id || agentId);
        } else {
          // Check for draft limit error
          const errorData = await response.json().catch(() => ({}));
          if (errorData.detail?.error_code === 'DRAFT_AGENT_LIMIT_REACHED') {
            setDraftLimitModal({
              show: true,
              draftCount: errorData.detail.draft_count,
              maxDrafts: errorData.detail.max_draft_agents
            });
          }
        }
      } catch (error) {
        console.error('Auto-save failed:', error);
        // Don't show error toast - auto-save should be silent
      }
    }, 2000); // 2 second debounce

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, agentConfig, agentId, saving, activeOrganization, hashConfig]);

  // Load source data based on entry point
  useEffect(() => {
    const loadSourceData = async () => {
      if (!activeOrganization?.organization_id) return;
      
      if (entryPoint === 'opportunity' && sourceId) {
        await loadOpportunity(sourceId);
      } else if (entryPoint === 'template' && sourceId) {
        await loadTemplate(sourceId);
      } else if (entryPoint === 'clone' && sourceId) {
        await loadExistingAgent(sourceId);
      } else if (agentId) {
        await loadExistingAgent(agentId);
      } else if (entryPoint === 'scratch') {
        // Start from scratch - open config tab with empty state
        setShowEntrySelector(false);
        setChatHistory([{
          role: 'assistant',
          content: `Welcome to manual agent configuration.\n\nYou have full control over all settings. Use the tabs above to:\n- **Config**: Set inputs, capabilities, and bindings\n- **Visual Flow**: See and edit your agent's flow diagram\n- **AI Chat**: Get AI assistance anytime\n\nStart by configuring your agent's name and capabilities in the Config tab.`,
          timestamp: new Date()
        }]);
      } else if (entryPoint === 'ai') {
        // AI builder - open visual flow tab (use Command for AI chat editing)
        setShowEntrySelector(false);
        setMode('visual');
        setInitialConfigHash(hashConfig(agentConfig));
        setChatHistory([{
          role: 'assistant',
          content: `✨ **Welcome to the AI Agent Builder!**\n\nJust describe what you want your agent to do in plain language, and I'll help you build it.\n\n**Examples:**\n• "I need an agent that reads PDF invoices and extracts line items"\n• "Build an automation that monitors webhooks and sends notifications"\n• "Create a customer support bot that answers questions"\n\n**Note:** For phone-based voice agents (S2S), I'll suggest our specialized Voice Wizard.\n\n**Tip:** You can switch to the **Config tab** anytime for manual control over all settings.\n\nWhat would you like to create?`,
          timestamp: new Date()
        }]);
      }
    };
    
    loadSourceData();
  }, [entryPoint, sourceId, agentId, activeOrganization]);

  // Re-fetch agent config when Command modifies it
  useEffect(() => {
    if (agentModifiedSignal > 0 && agentId && activeOrganization?.organization_id) {
      // Use the same loader but skip the loading spinner for seamless refresh
      const refreshAfterModify = async () => {
        try {
          const response = await fetch(
            `${BACKEND_URL}/api/agents/${agentId}`,
            { credentials: 'include', headers: { 'X-Organization-Id': activeOrganization.organization_id } }
          );
          if (response.ok) {
            const agent = await response.json();
            setSourceData({ type: 'agent', data: agent });
            const newConfig = {
              name: agent.name,
              description: agent.short_description || agent.description || '',
              capability_profile: agent.capability_profile || 'text_agent',
              accepted_inputs: agent.accepted_inputs || ['text'],
              required_inputs: agent.required_inputs || [],
              expected_outputs: agent.expected_outputs || agent.expected_output || agent.output_schema?.types || [],
              required_capabilities: agent.required_capabilities || [],
              optional_capabilities: agent.optional_capabilities || [],
              capability_bindings: agent.capability_bindings || {},
              system_prompt: agent.system_prompt || agent.agent_config?.config?.system_prompt || agent.agent_config?.system_prompt || '',
              is_active: agent.is_active,
              status: agent.status,
              source_type: agent.source_type || (agent.installed_from_template ? 'template' : 'manual'),
              installed_from_template: agent.installed_from_template,
              output_schema: agent.output_schema,
              llm_model: agent.llm_model,
              mcp_compliant: agent.mcp_compliant,
              use_custom_ai_model: agent.use_custom_ai_model || false,
              custom_ai_config: agent.custom_ai_config || null,
              flow: agent.flow || null,
              workflow_state: agent.workflow_state || null,
            };
            setAgentConfig({ ...newConfig });
            setInitialConfigHash(hashConfig(newConfig));
            setHasUnsavedChanges(false);
          }
        } catch (e) {
          console.error('Failed to refresh agent after Command modification:', e);
        }
      };
      refreshAfterModify();
    }
  }, [agentModifiedSignal]);


  const loadOpportunity = async (oppId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/process/opportunities/${oppId}`,
        {
          credentials: 'include',
          headers: { 'X-Organization-Id': activeOrganization.organization_id }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        const opp = data.opportunity || data;
        
        // IMPORTANT: Pass structured context, NOT raw config
        // Per consolidation spec - let the builder guide completion
        setSourceData({ 
          type: 'opportunity', 
          data: opp,
          context: {
            source: 'opportunity',
            opportunity_id: oppId,
            prefill: {
              name: opp.title || '',
              description: opp.one_liner || opp.description || '',
              required_capabilities: opp.required_capabilities || [],
              optional_capabilities: opp.optional_capabilities || [],
              capabilities_locked: true // Lock capabilities from opportunity
            }
          }
        });
        
        // Lock capabilities (from opportunity spec)
        setCapabilitiesLocked(true);
        
        // Prefill agent config with structured context
        const newConfig = {
          ...agentConfig,
          name: opp.title || '',
          description: opp.one_liner || opp.description || '',
          required_capabilities: opp.required_capabilities || [],
          optional_capabilities: opp.optional_capabilities || [],
          // Derive inputs/outputs from IAO if available
          accepted_inputs: opp.iao?.input?.accepted_inputs || ['text'],
          expected_outputs: opp.iao?.output?.outputs_declared?.map(o => ({ label: o, type: 'text' })) || [],
          capability_profile: opp.capability_profile || 'text_agent',
          is_active: false,
          status: 'draft'
        };
        
        setAgentConfig(newConfig);
        setInitialConfigHash(hashConfig(newConfig));
        
        // Add initial chat message explaining the locked state
        setChatHistory([{
          role: 'assistant',
          content: `🎯 **Building Agent from Opportunity**\n\n**"${opp.title}"**\n${opp.one_liner || ''}\n\n**This agent is buildable** because the required capabilities are supported.\n\n**Locked Capabilities** (from opportunity analysis):\n${(opp.required_capabilities || []).map(c => `• ${c.replace(/_/g, ' ')}`).join('\n') || '• None specified'}\n\n**Next Steps:**\n1. Review the Visual Flow diagram\n2. Configure capability bindings in Config tab\n3. Add any optional capabilities\n4. Test your agent\n\n_Capabilities are structural and cannot be changed for this opportunity-based agent._`,
          timestamp: new Date()
        }]);
        
        // Switch to Visual Flow tab for opportunity-based agents
        setMode('visual');
        setShowEntrySelector(false);
      }
    } catch (error) {
      console.error('Error loading opportunity:', error);
      toast.error('Failed to load opportunity');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplate = async (templateId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/agent-templates/${templateId}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const template = await response.json();
        setSourceData({ type: 'template', data: template });
        
        const agentCfg = template.agent_config || {};
        
        // Prefill from template — include inputs, capabilities, and outputs for flow rendering
        setAgentConfig(prev => ({
          ...prev,
          name: template.name,
          description: template.description || '',
          capability_profile: template.capability_profile || 'text_agent',
          accepted_inputs: template.accepted_inputs || [],
          required_capabilities: template.required_capabilities || agentCfg.capabilities?.required || [],
          optional_capabilities: template.optional_capabilities || agentCfg.capabilities?.optional || [],
          expected_outputs: template.expected_outputs || [],
          system_prompt: template.system_prompt || agentCfg.system_prompt || '',
          source_type: 'template',
          installed_from_template: template.id,
        }));
        
        setChatHistory([{
          role: 'assistant',
          content: `I've loaded the "${template.name}" template with a complete Input \u2192 Process \u2192 Output flow.\n\nWould you like to:\n1. Customize the agent name and description\n2. Modify the capabilities or flow\n3. Review the system prompt`,
          timestamp: new Date()
        }]);
        
        setShowEntrySelector(false);
      }
    } catch (error) {
      console.error('Error loading template:', error);
      toast.error('Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const loadExistingAgent = async (id) => {
    try {
      setLoading(true);
      const response = await fetch(
        `${BACKEND_URL}/api/agents/${id}`,
        {
          credentials: 'include',
          headers: { 'X-Organization-Id': activeOrganization.organization_id }
        }
      );
      
      if (response.ok) {
        const agent = await response.json();
        setSourceData({ type: 'agent', data: agent });
        
        // Map agent fields to config - include all MCP fields
        const newConfig = {
          name: entryPoint === 'clone' ? `${agent.name} (Copy)` : agent.name,
          description: agent.short_description || agent.description || '',
          capability_profile: agent.capability_profile || 'text_agent',
          accepted_inputs: agent.accepted_inputs || ['text'],
          required_inputs: agent.required_inputs || [],
          // Support both field names for backwards compatibility
          expected_outputs: agent.expected_outputs || agent.expected_output || agent.output_schema?.types || [],
          required_capabilities: agent.required_capabilities || [],
          optional_capabilities: agent.optional_capabilities || [],
          capability_bindings: agent.capability_bindings || {},
          system_prompt: agent.system_prompt || agent.agent_config?.config?.system_prompt || agent.agent_config?.system_prompt || '',
          is_active: entryPoint === 'clone' ? false : agent.is_active,
          status: entryPoint === 'clone' ? 'draft' : agent.status,
          // Additional MCP fields
          source_type: agent.source_type || (agent.installed_from_template ? 'template' : 'manual'),
          installed_from_template: agent.installed_from_template,
          output_schema: agent.output_schema,
          llm_model: agent.llm_model,
          mcp_compliant: agent.mcp_compliant,
          // Agent-specific AI model override
          use_custom_ai_model: agent.use_custom_ai_model || false,
          custom_ai_config: agent.custom_ai_config || null,
          // V2 flow data (used by CapabilityFlowCanvas when present)
          flow: agent.flow || null,
          workflow_state: agent.workflow_state || null,
        };
        
        setAgentConfig(newConfig);
        setInitialConfigHash(hashConfig(newConfig));
        setHasUnsavedChanges(false);
        
        // Set appropriate chat message for editing an existing agent
        if (entryPoint !== 'clone') {
          setChatHistory([{
            role: 'assistant',
            content: `📝 **Editing: ${agent.name}**\n\nYou're editing an existing agent. Here's what you can do:\n\n• Use the **Visual Flow** tab to see and modify the agent's workflow\n• Use the **Config** tab to adjust settings and capabilities\n• Ask me anything here - I can help you:\n  - Modify the agent's behavior\n  - Add new capabilities\n  - Explain current configuration\n  - Troubleshoot issues\n\nWhat would you like to change?`,
            timestamp: new Date()
          }]);
        } else {
          // Cloning message
          setChatHistory([{
            role: 'assistant',
            content: `📋 **Cloning: ${agent.name}**\n\nI've created a copy of this agent. The clone is in draft status and not yet active.\n\nYou can:\n• Rename it in the **Config** tab\n• Modify its capabilities\n• Test it before activating\n\nWhat changes would you like to make to this copy?`,
            timestamp: new Date()
          }]);
        }
        
        // Load current version info
        if (entryPoint !== 'clone') {
          fetchLatestVersion(id);
        }
        
        setShowEntrySelector(false);
      }
    } catch (error) {
      console.error('Error loading agent:', error);
      toast.error('Failed to load agent');
    } finally {
      setLoading(false);
    }
  };

  // Fetch latest version info
  const fetchLatestVersion = async (agentIdToFetch) => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/agents/${agentIdToFetch}/versions?limit=1`,
        {
          credentials: 'include',
          headers: { 'X-Organization-Id': activeOrganization.organization_id }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.versions?.length > 0) {
          setCurrentVersion(data.versions[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching version:', error);
    }
  };

  // Phase 4 B2: Open Command with this agent's context for editing
  const handleEditWithCommand = useCallback(() => {
    if (!agentConfig?.name) return;
    openCommandForAgent({
      agent_id: agentId,
      id: agentId,
      name: agentConfig.name,
      workflow_state: sourceData?.data?.workflow_state || null,
    });
  }, [agentId, agentConfig?.name, sourceData, openCommandForAgent]);


  // Handle entry point selection
  const handleEntryPointSelect = (point, data) => {
    if (point === 'opportunity' && data?.id) {
      // If data object provided directly, use it to pre-fill
      if (data?.data) {
        const opp = data.data;
        
        // Structured context per consolidation spec
        setSourceData({ 
          type: 'opportunity', 
          data: opp,
          context: {
            source: 'opportunity',
            opportunity_id: data.id,
            prefill: {
              name: opp.title || '',
              description: opp.one_liner || opp.description || '',
              required_capabilities: opp.suggested_capabilities || opp.required_capabilities || [],
              optional_capabilities: opp.optional_capabilities || [],
              capabilities_locked: true
            }
          }
        });
        
        // Lock capabilities from opportunity
        setCapabilitiesLocked(true);
        
        const newConfig = {
          ...agentConfig,
          name: opp.suggested_agent_name || opp.title || '',
          description: opp.one_liner || opp.description || '',
          capability_profile: opp.capability_profile || 'text_agent',
          accepted_inputs: opp.accepted_inputs || ['text'],
          required_inputs: opp.required_inputs || [],
          expected_outputs: opp.expected_outputs || [],
          required_capabilities: opp.suggested_capabilities || opp.required_capabilities || [],
          optional_capabilities: opp.optional_capabilities || [],
          capability_bindings: {},
          system_prompt: opp.suggested_system_prompt || '',
          is_active: false,
          status: 'draft'
        };
        setAgentConfig(newConfig);
        setInitialConfigHash(hashConfig(newConfig));
        
        setChatHistory([{
          role: 'assistant',
          content: `🎯 **Building Agent from Opportunity**\n\n**"${opp.title}"**\n${opp.one_liner || ''}\n\n**This agent is buildable** because the required capabilities are supported.\n\n**Locked Capabilities:**\n${(opp.suggested_capabilities || opp.required_capabilities || []).map(c => `• ${c.replace(/_/g, ' ')}`).join('\n') || '• None specified'}\n\nReview the Visual Flow and configure capability bindings in the Config tab.`,
          timestamp: new Date()
        }]);
        
        // Open Visual Flow tab for opportunity
        setMode('visual');
        setShowEntrySelector(false);
      } else {
        // Navigate to load opportunity by ID
        navigate(`/unified-agent-builder?from=opportunity&sourceId=${data.id}`);
      }
    } else if (point === 'template' && data?.id) {
      // If data object provided directly, use it to pre-fill
      if (data?.data) {
        const tmpl = data.data;
        setSourceData({ type: 'template', data: tmpl });
        
        const newConfig = {
          ...agentConfig,
          name: tmpl.name || '',
          description: tmpl.description || '',
          capability_profile: tmpl.capability_profile || 'text_agent',
          accepted_inputs: tmpl.accepted_inputs || ['text'],
          required_inputs: tmpl.required_inputs || [],
          expected_outputs: tmpl.expected_outputs || [],
          required_capabilities: tmpl.required_capabilities || tmpl.key_capabilities || [],
          optional_capabilities: tmpl.optional_capabilities || [],
          capability_bindings: {},
          system_prompt: tmpl.system_prompt || '',
          is_active: false,
          status: 'draft'
        };
        setAgentConfig(newConfig);
        setInitialConfigHash(hashConfig(newConfig));
        
        setChatHistory([{
          role: 'assistant',
          content: `📋 **Template Loaded: ${tmpl.name}**\n\n${tmpl.description || ''}\n\n${tmpl.key_capabilities?.length ? `**Capabilities:**\n${tmpl.key_capabilities.slice(0, 6).map(c => `• ${c.replace(/_/g, ' ')}`).join('\n')}` : ''}\n\nYou can customize this template. Review the Visual Flow and adjust as needed.`,
          timestamp: new Date()
        }]);
        
        // Open Visual Flow tab for template
        setMode('visual');
        setShowEntrySelector(false);
      } else {
        // Navigate to load template by ID
        navigate(`/unified-agent-builder?from=template&sourceId=${data.id}`);
      }
    } else if (point === 'voice') {
      // Voice agents are handled by EntryPointSelector directly
      toast.info('Voice agents use a specialized wizard');
    } else if (point === 'operator') {
      // Operator agents - open the CreateOperatorModal
      setShowEntrySelector(false);
      setShowOperatorModal(true);
    } else if (point === 'ai') {
      // AI Builder → AI Chat tab - Reset state for a fresh start
      setShowEntrySelector(false);
      const freshConfig = {
        name: '',
        description: '',
        capability_profile: 'text_agent',
        accepted_inputs: [],
        required_inputs: [],
        expected_outputs: [],
        required_capabilities: [],
        optional_capabilities: [],
        capability_bindings: {},
        system_prompt: '',
        is_active: false,
        status: 'draft'
      };
      setAgentConfig(freshConfig);
      setInitialConfigHash(hashConfig(freshConfig));
      setSourceData(null);
      setCapabilitiesLocked(false);
      setChatHistory([{
        role: 'assistant',
        content: `Hi! I'm here to help you build a custom AI agent. 🤖\n\nDescribe what you want your agent to do, and I'll help create the configuration.\n\nFor example:\n• "Create an agent that summarizes meeting notes"\n• "Build a customer support bot for my e-commerce store"\n• "I need an agent to analyze documents and extract key information"`,
        timestamp: new Date()
      }]);
      
      // Navigate with param so sidebar knows we're in builder
      navigate('/unified-agent-builder?from=ai', { replace: true });
    }
  };

  // Update agent config from chat
  const handleConfigUpdate = useCallback((updates) => {
    setAgentConfig(prev => ({
      ...prev,
      ...updates
    }));
  }, []);

  // FloppyDisk agent
  const handleSave = async (deploy = false) => {
    if (!agentConfig.name.trim()) {
      toast.error('Please provide an agent name');
      return;
    }
    
    setSaving(true);
    try {
      // When deploying, we save as draft and redirect to Agent Details for payment
      // The activation happens on the Agent Details page after payment
      const payload = {
        name: agentConfig.name,
        short_description: agentConfig.description,
        capability_profile: agentConfig.capability_profile,
        accepted_inputs: agentConfig.accepted_inputs,
        required_inputs: agentConfig.required_inputs,
        expected_output: agentConfig.expected_outputs,
        required_capabilities: agentConfig.required_capabilities,
        optional_capabilities: agentConfig.optional_capabilities,
        capability_bindings: agentConfig.capability_bindings,
        agent_config: {
          system_prompt: agentConfig.system_prompt
        },
        // Agent-specific AI model override
        use_custom_ai_model: agentConfig.use_custom_ai_model || false,
        custom_ai_config: agentConfig.use_custom_ai_model ? {
          provider: agentConfig.custom_ai_config?.provider || null,
          model: agentConfig.custom_ai_config?.model || null,
          api_key: agentConfig.custom_ai_config?.api_key || null
        } : null,
        // Source opportunity tracking
        source_opportunity_id: entryPoint === 'opportunity' && sourceId ? sourceId : null,
        // Always save as draft - activation happens on Agent Details page after payment
        is_active: false,
        status: 'draft',
        org_id: activeOrganization.organization_id
      };
      
      const url = agentId 
        ? `${BACKEND_URL}/api/agents/${agentId}`
        : `${BACKEND_URL}/api/agents`;
      
      const response = await fetch(url, {
        method: agentId ? 'PUT' : 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-Organization-Id': activeOrganization.organization_id
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const result = await response.json();
        const savedAgentId = result.id || result.agent_id || agentId;
        
        // Auto-save version
        if (savedAgentId && hasUnsavedChanges) {
          await saveVersion(savedAgentId, deploy ? 'Ready for deployment' : 'Draft saved');
        }
        
        // Update hash to mark as saved
        setInitialConfigHash(hashConfig(agentConfig));
        setHasUnsavedChanges(false);
        
        // Always navigate to Agent Details page after save/deploy
        // Agent Details page handles activation, payment, pause/resume
        const message = deploy ? 'Agent saved! Redirecting to activation...' : 'Agent saved as draft';
        toast.success(message);
        navigate(`/partner-pro/ai-workforce/agents/${savedAgentId}`);
      } else {
        const errorData = await response.json().catch(() => ({}));
        
        // Check for draft limit error
        if (errorData.detail?.error_code === 'DRAFT_AGENT_LIMIT_REACHED') {
          setDraftLimitModal({
            show: true,
            draftCount: errorData.detail.draft_count,
            maxDrafts: errorData.detail.max_draft_agents
          });
        } else {
          toast.error(errorData.detail?.detail || errorData.detail || 'Failed to save agent');
        }
      }
    } catch (error) {
      console.error('Error saving agent:', error);
      toast.error('Failed to save agent');
    } finally {
      setSaving(false);
    }
  };

  // FloppyDisk a version of the agent configuration
  const saveVersion = async (targetAgentId, changeSummary) => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      await fetch(
        `${BACKEND_URL}/api/agents/${targetAgentId}/versions`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          body: JSON.stringify({
            agent_id: targetAgentId,
            config: agentConfig,
            change_summary: changeSummary,
            is_auto_save: true
          })
        }
      );
    } catch (error) {
      console.error('Error saving version:', error);
      // Don't show error toast - version save is supplementary
    }
  };

  // Handle quick action selection
  const handleQuickAction = useCallback((action) => {
    switch (action.action) {
      case 'focus_name':
        // Switch to config mode and show name field
        setMode('config');
        toast.info('Configure your agent name in the form below');
        break;
      case 'focus_description':
        setMode('config');
        toast.info('Add a description for your agent');
        break;
      case 'add_capability':
        // Add capability to config
        if (action.capability) {
          setAgentConfig(prev => ({
            ...prev,
            required_capabilities: [...new Set([...(prev.required_capabilities || []), action.capability])]
          }));
          toast.success(`Added ${action.capability.replace(/_/g, ' ')} capability`);
        }
        break;
      case 'open_test_console':
        // Scroll test console into view
        document.querySelector('[data-testid="simulation-input"]')?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        document.querySelector('[data-testid="simulation-input"]')?.focus();
        break;
      case 'open_ai_settings':
        // Navigate to AI configuration settings
        navigate('/settings?tab=ai');
        break;
      case 'open_mcp_settings':
        window.open('/settings/mcp', '_blank');
        break;
      case 'open_security_config':
        setMode('config');
        toast.info('Review security guidelines in the system prompt');
        break;
      default:
        console.log('Quick action:', action);
        toast.info(`Action: ${action.title || action.action}`);
    }
  }, [navigate]);

  // Run test with flow animation using backend execution
  // Uses execute-test for immediate animation, with WebSocket option for long-running flows
  const handleRunTest = async (input) => {
    if (!agentConfig.name) {
      toast.error('Please configure your agent first');
      return;
    }
    
    setTestInput(input);
    setTestLoading(true);
    setTestResult(null);
    setExecutionState({ phase: 'starting' });
    
    try {
      const startTime = Date.now();
      
      // Build the flow from current agent config (or use saved flow if available)
      const flow = sourceData?.data?.flow || {
        nodes: buildNodesFromConfig(agentConfig),
        edges: buildEdgesFromConfig(agentConfig)
      };
      
      // Use execute-test endpoint for immediate response with timeline
      const response = await fetch(
        `${BACKEND_URL}/api/flow/execute-test`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization?.organization_id
          },
          body: JSON.stringify({
            agent_id: agentId || null,
            flow: agentId ? null : flow,
            input_data: {
              textInput: input.textInput || '',
              urlInput: input.urlInput || '',
              agent_config: agentConfig
            },
            dry_run: false
          })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to execute flow');
      }
      
      const executionData = await response.json();
      const timeline = executionData.execution_timeline || [];
      
      // Animate through each node based on timeline
      for (let i = 0; i < timeline.length; i++) {
        setExecutionState({ 
          phase: 'processing', 
          currentNodeIndex: i,
          currentNodeId: timeline[i].node_id,
          nodeStatus: timeline[i].status,
          completedNodes: timeline.slice(0, i).map(t => t.node_id)
        });
        
        // Use delay from timeline (backend calculates realistic delays)
        const delay = (timeline[i].delay || 0.3) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      
      // Mark as complete
      setExecutionState({ 
        phase: 'complete', 
        currentNodeIndex: timeline.length,
        completedNodes: timeline.map(t => t.node_id)
      });
      
      // Calculate metrics
      const duration = ((Date.now() - startTime) / 1000).toFixed(1);
      const metrics = executionData.metrics || {};
      
      // Extract output text
      let outputText = executionData.output;
      if (typeof outputText === 'object') {
        outputText = outputText.output || outputText.text || JSON.stringify(outputText, null, 2);
      }
      
      setTestResult(outputText || generateTestResponse(input));
      setExecutionMetrics({
        tokens: metrics.estimated_tokens || 0,
        duration: `${duration}s`,
        cost: `$${(metrics.estimated_cost || 0).toFixed(4)}`,
        nodesExecuted: metrics.nodes_completed || timeline.length,
        nodesTotal: metrics.nodes_total || timeline.length,
        runId: executionData.run_id
      });
      
      // Show result modal after brief delay
      setTimeout(() => {
        setShowResultModal(true);
      }, 500);
      
      // Reset execution state after showing results
      setTimeout(() => {
        setExecutionState(null);
      }, 3000);
      
    } catch (error) {
      console.error('Test execution error:', error);
      setExecutionState(null);
      setTestResult({ success: false, error: error.message });
      toast.error(`Test execution failed: ${error.message}`);
    } finally {
      setTestLoading(false);
    }
  };
  
  // Helper: Build nodes array from agent config for flow execution
  const buildNodesFromConfig = (config) => {
    const nodes = [];
    const inputs = config.accepted_inputs || ['text'];
    const capabilities = [
      ...(config.required_capabilities || []),
      ...(config.optional_capabilities || [])
    ];
    const outputs = config.expected_outputs || ['text'];
    
    // Input nodes
    inputs.forEach((input, idx) => {
      nodes.push({
        id: `input-${idx}`,
        type: `input.${input}`,
        label: `${input} Input`,
        position: { x: 100, y: idx * 120 }
      });
    });
    
    // Capability nodes
    capabilities.forEach((cap, idx) => {
      nodes.push({
        id: `cap-${idx}`,
        type: 'capability.invoke',
        label: cap.replace(/_/g, ' '),
        params: { capability: cap },
        position: { x: 400, y: idx * 120 }
      });
    });
    
    // Output nodes
    outputs.forEach((output, idx) => {
      const label = typeof output === 'string' ? output : output.label || 'Output';
      nodes.push({
        id: `output-${idx}`,
        type: 'output.display',
        label: label,
        position: { x: 700, y: idx * 120 }
      });
    });
    
    return nodes;
  };
  
  // Helper: Build edges array from agent config
  const buildEdgesFromConfig = (config) => {
    const edges = [];
    const inputs = config.accepted_inputs || ['text'];
    const capabilities = [
      ...(config.required_capabilities || []),
      ...(config.optional_capabilities || [])
    ];
    const outputs = config.expected_outputs || ['text'];
    
    // Connect inputs to first capability
    if (capabilities.length > 0) {
      inputs.forEach((_, inputIdx) => {
        edges.push({
          id: `e-input${inputIdx}-cap0`,
          source: `input-${inputIdx}`,
          target: 'cap-0'
        });
      });
      
      // Chain capabilities
      for (let i = 1; i < capabilities.length; i++) {
        edges.push({
          id: `e-cap${i-1}-cap${i}`,
          source: `cap-${i-1}`,
          target: `cap-${i}`
        });
      }
      
      // Connect last capability to outputs
      const lastCapIdx = capabilities.length - 1;
      outputs.forEach((_, outputIdx) => {
        edges.push({
          id: `e-cap${lastCapIdx}-output${outputIdx}`,
          source: `cap-${lastCapIdx}`,
          target: `output-${outputIdx}`
        });
      });
    } else {
      // No capabilities - connect inputs directly to outputs
      inputs.forEach((_, inputIdx) => {
        outputs.forEach((_, outputIdx) => {
          edges.push({
            id: `e-input${inputIdx}-output${outputIdx}`,
            source: `input-${inputIdx}`,
            target: `output-${outputIdx}`
          });
        });
      });
    }
    
    return edges;
  };

  // Generate test response based on agent config
  const generateTestResponse = (input) => {
    const agentName = agentConfig.name || 'Agent';
    const capabilities = agentConfig.required_capabilities || [];
    
    let response = `## ${agentName} Response\n\n`;
    
    if (capabilities.includes('summarization') || capabilities.includes('text_processing')) {
      response += `### Summary\n`;
      response += `Based on the input provided, here are the key points:\n\n`;
      response += `- **Main Topic**: Analysis of the provided content\n`;
      response += `- **Key Insights**: Multiple important elements identified\n`;
      response += `- **Recommendations**: Further action items suggested below\n\n`;
    }
    
    if (capabilities.includes('extraction') || capabilities.includes('structured_extraction')) {
      response += `### Extracted Information\n`;
      response += `| Field | Value |\n`;
      response += `|-------|-------|\n`;
      response += `| Type | Document |\n`;
      response += `| Date | ${new Date().toLocaleDateString()} |\n`;
      response += `| Status | Processed |\n\n`;
    }
    
    response += `### Processing Complete\n`;
    response += `The ${agentName} has successfully processed your request. `;
    response += `All configured capabilities have been applied.\n\n`;
    response += `*This is a simulated response for testing purposes.*`;
    
    return response;
  };

  // Render left panel based on mode
  const renderLeftPanel = () => {
    switch (mode) {
      case 'visual':
        return (
          <CapabilityFlowCanvas
            agentConfig={agentConfig}
            onConfigUpdate={handleConfigUpdate}
            readOnly={false}
            executionState={executionState}
            highlightNodeIds={highlightNodeIds}
          />
        );
      case 'config':
        return (
          <ConfigPanel
            agentConfig={agentConfig}
            onConfigUpdate={handleConfigUpdate}
            capabilitiesLocked={capabilitiesLocked}
            sourceData={sourceData}
            agentId={agentId}
            onSave={handleSave}
            hasUnsavedChanges={hasUnsavedChanges}
          />
        );
      default:
        return (
          <CapabilityFlowCanvas
            agentConfig={agentConfig}
            onConfigUpdate={handleConfigUpdate}
            readOnly={false}
            executionState={executionState}
            highlightNodeIds={highlightNodeIds}
          />
        );
    }
  };

  // Show entry point selector
  if (showEntrySelector) {
    return (
      <div className="min-h-screen bg-[#0a0b0f]">
        <BuilderHeader 
          title="Create New Agent"
          onBack={() => navigate(-1)}
        />
        <EntryPointSelector onSelect={handleEntryPointSelect} />
      </div>
    );
  }

  // Handle revert from version history
  const handleVersionRevert = (revertedVersion) => {
    // Update local config state with reverted config
    if (revertedVersion?.config) {
      setAgentConfig(prev => ({
        ...prev,
        ...revertedVersion.config
      }));
    }
    // Reload the agent to get updated data
    if (agentId) {
      loadExistingAgent(agentId);
    }
  };

  // Handle back navigation - return to entry selector for new agents, or go back for existing
  const handleBack = () => {
    if (agentId) {
      // Editing existing agent - go back to previous page
      navigate(-1);
    } else {
      // New agent - return to entry point selector
      setShowEntrySelector(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0b0f] flex flex-col">
      <BuilderHeader 
        title={agentConfig.name || 'New Agent'}
        status={agentConfig.status}
        onBack={handleBack}
        onSave={() => handleSave(false)}
        onDeploy={() => handleSave(true)}
        onEditWithCommand={agentId ? handleEditWithCommand : undefined}
        onSubmitToMarketplace={agentId ? () => setShowSubmissionModal(true) : undefined}
        marketplaceStatus={marketplaceStatus}
        saving={saving}
        showVersionHistory={agentId ? true : false}
        onToggleVersionHistory={() => setShowVersionHistory(!showVersionHistory)}
        versionHistoryActive={showVersionHistory}
        currentVersion={currentVersion}
        hasUnsavedChanges={hasUnsavedChanges}
        lastAutoSave={lastAutoSave}
      />

      {/* Marketplace Submission Modal */}
      <SubmissionModal
        isOpen={showSubmissionModal}
        onClose={() => setShowSubmissionModal(false)}
        agentId={agentId}
        agentConfig={agentConfig}
        onSubmissionChange={(sub) => setMarketplaceStatus(sub?.status || null)}
      />
      
      <BuilderLayout
        mode={mode}
        onModeChange={setMode}
        leftPanel={renderLeftPanel()}
        rightPanel={
          <div className="flex flex-col h-full">
            {/* Version ClockCounterClockwise Panel (when active) */}
            {showVersionHistory && agentId && (
              <div className="h-[35%] border-b border-white/10 overflow-hidden">
                <VersionHistoryPanel
                  agentId={agentId}
                  agentConfig={agentConfig}
                  onRevert={handleVersionRevert}
                  onVersionSaved={() => {}}
                  onToggle={() => setShowVersionHistory(false)}
                />
              </div>
            )}
            
            {/* Live Preview - Takes 55% */}
            <div className="h-[55%] border-b border-white/10 overflow-hidden">
              <LivePreviewPanel 
                agentConfig={agentConfig}
                onConfigUpdate={handleConfigUpdate}
                onSectionFocus={(sectionId) => {
                  // Map progress section IDs to config panel sections
                  const sectionMap = {
                    'identity': 'identity',
                    'inputs': 'inputs',
                    'capabilities': 'capabilities',
                    'logic': 'advanced', // System prompt is in Advanced GearSix
                    'output': 'output',  // Output Schema section
                    'config': 'capabilities' // LLM config is in Capabilities
                  };
                  
                  // Sections to auto-expand when navigating
                  const expandMap = {
                    'identity': 'identity',
                    'inputs': 'inputs',
                    'capabilities': 'capabilities',
                    'logic': 'advanced',
                    'output': 'output-schema',
                    'config': 'capabilities'
                  };
                  
                  // Switch to config mode if not already there
                  setMode('config');
                  
                  const targetSection = sectionMap[sectionId] || sectionId;
                  
                  // Scroll to section after a brief delay to allow mode switch
                  setTimeout(() => {
                    const element = document.getElementById(`config-section-${targetSection}`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      // Add a brief highlight effect
                      element.classList.add('ring-2', 'ring-indigo-500/50');
                      setTimeout(() => {
                        element.classList.remove('ring-2', 'ring-indigo-500/50');
                      }, 2000);
                    }
                  }, 150);
                }}
              />
            </div>
            
            {/* Test Input Panel - Takes 45% */}
            <div className="h-[45%] overflow-hidden">
              <TestInputPanel
                agentConfig={agentConfig}
                agentId={agentId}
                onRunTest={handleRunTest}
                isRunning={testLoading}
                isCollapsed={testInputCollapsed}
                onToggleCollapse={() => setTestInputCollapsed(!testInputCollapsed)}
                initialInput={testInput}
              />
            </div>

            {/* Run ClockCounterClockwise Panel */}
            {agentId && (
              <RunHistoryPanel
                agentId={agentId}
                isExpanded={showRunHistory}
                onToggle={() => setShowRunHistory(!showRunHistory)}
                onReplayRun={(inputData) => {
                  // Pre-fill test input with data from historical run
                  setTestInputCollapsed(false);
                  setTestInput(inputData);
                }}
              />
            )}
          </div>
        }
        quickActionsPanel={
          <QuickActionsPanel
            agentConfig={agentConfig}
            onAction={handleQuickAction}
          />
        }
      />

      {/* Test Result Modal - Shows after flow animation completes */}
      <TestResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        result={testResult}
        metrics={executionMetrics}
        input={testInput}
      />

      {/* Draft Limit Reached Modal */}
      <AnimatePresence>
        {draftLimitModal.show && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onClick={() => setDraftLimitModal({ ...draftLimitModal, show: false })}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md mx-4 p-6 rounded-2xl bg-[#1a1b1f] border border-white/10 shadow-2xl"
              data-testid="draft-limit-modal"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 rounded-lg bg-amber-500/20">
                  <Warning className="w-5 h-5 text-amber-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Draft limit reached</h3>
              </div>
              
              <p className="text-sm text-slate-400 mb-6">
                You can have up to {draftLimitModal.maxDrafts} draft AI agents at a time.
                <br />
                Delete an existing draft to create a new one.
              </p>
              
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => {
                    setDraftLimitModal({ ...draftLimitModal, show: false });
                    navigate('/partner-pro/ai-workforce/agents?status=draft');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg
                    bg-indigo-500 hover:bg-indigo-600 text-white font-medium transition-colors"
                  data-testid="view-draft-agents-btn"
                >
                  <FileText size={16} />
                  View Draft Agents
                </button>
                
                <button
                  onClick={() => setDraftLimitModal({ ...draftLimitModal, show: false })}
                  className="w-full px-4 py-2.5 rounded-lg text-slate-400 hover:text-white 
                    hover:bg-white/5 font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Operator Agent Creation Modal */}
      <CreateOperatorModal
        isOpen={showOperatorModal}
        onClose={() => setShowOperatorModal(false)}
        onSuccess={() => {
          setShowOperatorModal(false);
          toast.success('Operator created successfully!');
        }}
        orgId={activeOrganization?.organization_id}
        navigateToOperator={(operatorId) => {
          setShowOperatorModal(false);
          navigate(`/partner-pro/ai-workforce/operators/${operatorId}`);
        }}
      />
    </div>
  );
};

export default UnifiedAgentBuilder;
